import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  setOppgave,
  setChangelog,
  clearCurrentOppgave,
  setLoading,
  setError,
  setErrorStatusCode,
  setSkjulSamtaleStatus,
  setPaminnelseEnabled,
} from "../slices/slice-oppgaveDetaljer";
import {
  OPPGAVE_INTERNAL,
  OPPGAVE_ENDPOINT_FULL,
  OPPGAVE_ENDPOINT,
  SKJUL_FOR_FULLMAKT,
  CHANGELOG,
  type SkjulOppgaveForFullmaktResponse,
} from "../../types/api-types";
import type { RootState } from "../reducer";
import type { UpdateOppgavePaminnelseRequest } from "../../types/request-types";
import {
  HelsenorgeProxyError,
  put,
  createHeaders,
} from "@helsenorge/framework-utils/hn-proxy-service";
import { getAbsoluteUrl } from "../../utils/test-utils";

export const fetchOppgave = createAsyncThunk(
  "oppgaver/fetchOppgave",
  async (oppgaveId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(false));
      dispatch(setErrorStatusCode(undefined));

      const url = getAbsoluteUrl(`${OPPGAVE_ENDPOINT_FULL}/${oppgaveId}`);
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        dispatch(setError(true));
        dispatch(setErrorStatusCode(response.status));
        throw new Error(`Network error ${response.status}`);
      }

      dispatch(setOppgave(data));
      return data;
    } catch (error) {
      console.error("Feil ved henting av oppgave:", error);
      dispatch(setError(true));
      throw error;
    }
  },
);

export const fetchChangelog = createAsyncThunk(
  "oppgaver/fetchChangelog",
  async (oppgaveId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(false));
      dispatch(setErrorStatusCode(undefined));

      const url = getAbsoluteUrl(
        `${OPPGAVE_ENDPOINT_FULL}/${oppgaveId}/${CHANGELOG}`,
      );
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        dispatch(setError(true));
        dispatch(setErrorStatusCode(response.status));
        throw new Error(`Network error ${response.status}`);
      }

      const changelog = data?.Changelog ?? [];
      dispatch(setChangelog(changelog));
      return changelog;
    } catch (error) {
      console.error("Feil ved henting av changelog:", error);
      dispatch(setError(true));
      throw error;
    }
  },
);

export const updateOppgavePaminnelse = createAsyncThunk<
  object,
  UpdateOppgavePaminnelseRequest,
  { state: RootState }
>(
  "updateoppgave/paminnelse",
  async ({ OppgaveGuid, SkalPaaminnes }, { dispatch }) => {
    try {
      const res = await put(
        OPPGAVE_INTERNAL,
        "api/v1/UpdateOppgave/paminnelse",
        { OppgaveGuid, SkalPaaminnes },
      );
      dispatch(setPaminnelseEnabled(SkalPaaminnes));
      return res;
    } catch (error) {
      if (error instanceof HelsenorgeProxyError) {
        return error.message;
      }
      return "error";
    }
  },
  {
    condition: (args, { getState }) => {
      const state = getState() as RootState;
      return !state.oppgaveDetaljer.isLoadingPaminnelse && !!args.OppgaveGuid;
    },
  },
);

export const updateOppgaveStatus = createAsyncThunk<
  object,
  string,
  { state: RootState }
>("updateoppgave/complete", async (Id) => {
  try {
    return await put(
      OPPGAVE_INTERNAL,
      `api/v1/UpdateOppgave/complete?oppgaveGuid=${Id}`,
    );
  } catch (error) {
    if (error instanceof HelsenorgeProxyError) {
      return error.message;
    }
    return "error";
  }
});

export const skjulOppgaveForFullmakt = createAsyncThunk<
  SkjulOppgaveForFullmaktResponse | null | string,
  { oppgaveGuid: string; skjulForFullmakt: boolean },
  { state: RootState }
>(
  "updateoppgave/skjulForFullmakt",
  async ({ oppgaveGuid, skjulForFullmakt }, { dispatch }) => {
    try {
      const res = await put(OPPGAVE_ENDPOINT, SKJUL_FOR_FULLMAKT, {
        OppgaveGuid: oppgaveGuid,
        SkjulForFullmakt: skjulForFullmakt,
      });
      const response = res as SkjulOppgaveForFullmaktResponse | null;

      if (!skjulForFullmakt) {
        dispatch(setSkjulSamtaleStatus(null));
      } else if (response?.SkjulSamtaleStatus !== undefined) {
        dispatch(setSkjulSamtaleStatus(response.SkjulSamtaleStatus));
      }

      return response;
    } catch (error) {
      if (error instanceof HelsenorgeProxyError) {
        return error.message;
      }
      return "error";
    }
  },
);

export const deleteOppgave = createAsyncThunk(
  "oppgaver/deleteOppgave",
  async (oppgaveGuid: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(false));
      dispatch(setErrorStatusCode(undefined));

      const url = getAbsoluteUrl(`${OPPGAVE_ENDPOINT_FULL}/${oppgaveGuid}`);
      const response = await fetch(url, {
        method: "DELETE",
        headers: createHeaders(),
      });

      if (!response.ok) {
        dispatch(setError(true));
        dispatch(setErrorStatusCode(response.status));
        throw new Error(`Network error ${response.status}`);
      }

      dispatch(clearCurrentOppgave());
      dispatch(setLoading(false));
      return true;
    } catch (error) {
      console.error("Feil ved sletting av oppgave:", error);
      dispatch(setError(true));
      throw error;
    }
  },
);
